// pages/admin/payroll/OneTimePayroll.jsx

import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Gift, Plus, Search } from "lucide-react";
import { toast } from "sonner";

import {
  getOneTimePayrollRuns,
  createOneTimePayroll,
  getPayrollRunsByType,
  lockPayrollRun,
  completePayRun,
  deletePayrollRun,
} from "@/services/payroll.service";

import { PayrollRunTable } from "./PayrollRunTable";
import ConfirmDialog from "@/components/ConfirmDialog";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PayrollSheet } from "@/components/admin/payroll/PayrollSheet";
import { formatCurrency } from "@/lib/utils";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

const OneTimePayroll = () => {
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [openSheet, setOpenSheet] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPayrollTable, setShowPayrollTable] = useState(false);
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);

  const [form, setForm] = useState({
    title: "",
    reason_type: "",
    payout_date: "",
    description: "",
  });

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    try {
      const data = await getPayrollRunsByType("one_time");
      setRuns(data || []);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load one-time payroll runs"));
      }
      setRuns([]);
    }
  };

  const handleCreate = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      await createOneTimePayroll(form);
      toast.success("One-time payroll created");

      setForm({
        title: "",
        reason_type: "",
        payout_date: "",
        description: "",
      });

      setOpenCreateModal(false);
      loadRuns();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create one-time payroll");
    }
  };

  const handleLock = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      // 🔑 build full payload from selected IDs
      const selectedEmployeePayload = activeRun.employees.filter((emp) =>
        selectedEmployees.includes(emp.employee_id),
      );

      if (selectedEmployeePayload.length === 0) {
        toast.error("No employees selected");
        return;
      }

      await lockPayrollRun(activeRun._id, selectedEmployeePayload);

      setActiveRun((prev) => ({
        ...prev,
        status: "locked",
        employees: selectedEmployeePayload, // optional but recommended
      }));

      await loadRuns();
      toast.success("Payroll locked");
    } catch (err) {
      console.error(err);
      toast.error("Failed to lock payroll");
    }
  };

  const handleComplete = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      await completePayRun(activeRun._id);
      toast.success("One-time payroll completed");
      setActiveRun(null);
      loadRuns();
    } catch {
      toast.error("Failed to complete payroll");
    }
  };

  const handleDeletePayrollRun = async () => {
    if (!canEdit("run_payroll", true)) return;
    if (!activeRun?._id) return;

    try {
      await deletePayrollRun(activeRun._id);

      toast.success("Termination payroll deleted successfully");

      // FULL RESET (same pattern as regular payroll)
      setActiveRun(null);
      setSelectedEmployees([]);
      setOpenSheet(false);
      setEditingEmployee(null);
      await loadRuns(); // reload list
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete termination payroll");
    }
  };

  const handleOpenRun = (run) => {
    if (!canEdit("run_payroll", true)) return;

    setActiveRun(run);
    setSelectedEmployees(run.employees.map((e) => e.employee_id));
    setShowPayrollTable(true);
  };

  const formatReasonType = (value = "") =>
    value
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const filteredEmployees =
    activeRun?.employees?.filter(
      (emp) =>
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.empId?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  /* --------------------------------------------
     LIST VIEW
  ---------------------------------------------*/
  if (!activeRun) {
    return (
      <>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-600" />
              One-Time Payrolls
            </CardTitle>

            <Button disabled={!canEdit("run_payroll")} onClick={() => setOpenCreateModal(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Create One-Time Payroll
            </Button>
          </CardHeader>

          <CardContent>
            {runs.length === 0 ? (
              <div className="flex flex-col items-center py-14 text-center">
                <Gift className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold">
                  No One-Time Payrolls Yet
                </h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md">
                  Use one-time payrolls to process bonuses, incentives, arrears,
                  or special payouts without affecting regular payroll.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left p-3">Title</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Payout Date</th>
                    <th className="text-center p-3">Employees</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {runs.map((run) => {
                    const overdue = run.is_overdue;
                    return (
                      <tr
                        key={run._id}
                        className="border-b hover:bg-muted/40 transition"
                      >
                        {/* TITLE */}
                        <td className="p-3 font-medium">
                          {run.title || "One-Time Payroll"}
                          {run.is_overdue && (
                            <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                              <AlertTriangle className="w-3 h-3" />
                              Payment overdue
                            </div>
                          )}
                        </td>

                        {/* TYPE */}
                        <td className="p-3">
                          {formatReasonType(run.reason_type)}
                        </td>

                        {/* PAYOUT DATE */}
                        <td className="p-3">
                          {run.payout_date
                            ? new Date(run.payout_date).toLocaleDateString()
                            : "-"}
                        </td>

                        {/* EMPLOYEES */}
                        <td className="p-3 text-center">
                          {run.employees?.length || 0}
                        </td>

                        {/* STATUS */}
                        <td className="p-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold capitalize
                            ${
                              overdue
                                ? "bg-red-100 text-red-700"
                                : run.status === "draft"
                                  ? "bg-gray-100 text-gray-700"
                                  : run.status === "locked"
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-emerald-100 text-emerald-700"
                            }`}
                          >
                            {overdue ? "Overdue" : run.status}
                          </span>
                        </td>

                        {/* ACTION */}
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant={overdue ? "destructive" : "default"}
                            disabled={!canEdit("run_payroll")}
                            onClick={() => handleOpenRun(run)}
                          >
                            {run.status === "locked"
                              ? "Disburse Now"
                              : "Continue"}
                          </Button>
                        </td>
                      </tr>
                    );}
                  )}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* CREATE ONE-TIME PAYROLL MODAL */}
        <Dialog open={openCreateModal} onOpenChange={setOpenCreateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create One-Time Payroll</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <Input
                placeholder="Payroll Title (e.g. Diwali Bonus)"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />

              <Select
                value={form.reason_type}
                onValueChange={(v) => setForm({ ...form, reason_type: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">Bonus</SelectItem>
                  <SelectItem value="incentive">Incentive</SelectItem>
                  <SelectItem value="arrears">Arrears</SelectItem>
                  <SelectItem value="ex_gratia">Ex-Gratia</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>

              <Input
                type="date"
                value={form.payout_date}
                onChange={(e) =>
                  setForm({ ...form, payout_date: e.target.value })
                }
              />

              <Input
                placeholder="Description (optional)"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOpenCreateModal(false)}
              >
                Cancel
              </Button>

              <Button
                onClick={handleCreate}
                disabled={!form.title || !form.reason_type}
              >
                Create Payroll
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  /* --------------------------------------------
     RUN VIEW
  ---------------------------------------------*/
  return (
    <>
      {activeRun.is_overdue && (
        <Card className="border-l-4 border-red-600 mb-4">
          <CardContent className="py-3 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700" />
            <div>
              <p className="font-semibold">One-Time Payment Overdue</p>
              <p className="text-xs">
                Payout date was{" "}
                <strong>
                  {new Date(activeRun.payout_date).toLocaleDateString()}
                </strong>
                . Please disburse immediately.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {/* PAYROLL SUMMARY */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Employees</p>
            <p className="text-2xl font-bold">{selectedEmployees.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Gross</p>
            <p className="text-2xl font-bold">
              {formatCurrency(
                activeRun.employees
                  .filter((e) => selectedEmployees.includes(e.employee_id))
                  .reduce((sum, e) => sum + Number(e.gross_pay || 0), 0),
              )}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 dark:bg-emerald-900/20">
          <CardContent className="pt-6">
            <p className="text-sm text-emerald-700">Net Payable</p>
            <p className="text-2xl font-bold text-emerald-700">
              {formatCurrency(
                activeRun.employees
                  .filter((e) => selectedEmployees.includes(e.employee_id))
                  .reduce((sum, e) => sum + Number(e.net_pay || 0), 0),
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* SEARCH */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* SELECT / DESELECT */}
            {activeRun.status !== "locked" && (
              <Button
                variant="outline"
                onClick={() =>
                  setSelectedEmployees(
                    selectedEmployees.length === activeRun.employees.length
                      ? []
                      : activeRun.employees.map((e) => e.employee_id),
                  )
                }
              >
                {selectedEmployees.length === activeRun.employees.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            )}

            {/* DELETE */}
            <Button
              variant="destructive"
              disabled={!canEdit("run_payroll")}
              onClick={() => setDeleteConfirm(true)}
            >
              Delete Payroll
            </Button>
          </div>
        </CardContent>
      </Card>

      <PayrollRunTable
        employees={filteredEmployees}
        selectedEmployees={selectedEmployees}
        setSelectedEmployees={setSelectedEmployees}
        payrollStatus={activeRun.status}
        isOneTime={true}
        onLock={handleLock}
        onComplete={handleComplete}
        onEditEmployee={(emp) => {
          if (!canEdit("run_payroll", true)) return;
          const latestEmployee = activeRun.employees.find(
            (e) => e.employee_id === emp.employee_id,
          );

          setEditingEmployee(latestEmployee);
          setOpenSheet(true);
        }}
      />

      <PayrollSheet
        openSheet={openSheet}
        setOpenSheet={setOpenSheet}
        selectedEmployee={editingEmployee}
        is_one_time={true}
        onSavePayroll={(updatedEmployee) => {
          setActiveRun((prev) => ({
            ...prev,
            employees: prev.employees.map((e) =>
              e.employee_id === updatedEmployee.employee_id
                ? updatedEmployee
                : e,
            ),
          }));
        }}
      />

      <ConfirmDialog
        open={deleteConfirm}
        onOpenChange={setDeleteConfirm}
        title="Delete One-Time Payroll?"
        destructive
        confirmText="Delete"
        onConfirm={async () => {
          await handleDeletePayrollRun();
        }}
      />
    </>
  );
};

export default OneTimePayroll;
